import React, { useEffect } from "react";

import "./body.css";
import "./fave.css";

const TipText = ({ tooltip, children }) => (
    <strong
        className="value-tooltip"
        data-tooltip={tooltip || ""}
        tabIndex={0}
    >
        {children}
    </strong>
);

function Criteria() {
    useEffect(() => {
        document.title = "Invest With Knowledge! - Criteria";
    }, []);

    return (
        <section className="mainbody">
            <h2 className="responsiveheader">Selection Criteria</h2>

            <div className="stockdiv fade-in">
                <h4>Chapter Fourteen Stocks — Defensive Investor</h4>
                <p>
                    Chapter Fourteen contains Graham’s most conservative rules. These filters are strict and
                    data-dependent. If essential information is missing, the stock does not qualify.
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Companies must not have negative year-over-year revenue growth.">
                        No Shrinking Revenue
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Shareholder equity must be known and greater than zero.">
                        Positive Equity
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Current price and shares outstanding must be present and positive.">
                        Valid Price & Share Count
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Net income (TTM) must be positive.">
                        Profitability Required
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="At least one must be available; if both are missing, the stock fails.">
                        Market Cap or Revenue Required
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Too much leverage disqualifies the stock.">
                        Debt-to-Equity ≤ 1
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="The company must comfortably cover interest expenses.">
                        Interest Coverage ≥ 2.5
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="If P/B is known and less than 6, then P/E × P/B must remain below 150.">
                        P/E × P/B Combination Test
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="P/E must be between 0 and 25.">
                        Three-Year P/E Requirement
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="If earnings velocity is known, it must be positive.">
                        Earnings Trend
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="If FCF is known, it must not be negative.">
                        Free Cash Flow
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Free cash flow must cover at least 20% of total debt when the metric is available.">
                        FCF-to-Debt ≥ 0.2
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="A dividend must exist.">
                        Dividend Required
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="If insiders are net sellers and selling exceeds buying by more than 4×, the stock fails.">
                        Insider Alignment
                    </TipText>
                </p>

                <p>
                    Additional rules apply based on sector classification: financials, REITs, utilities,
                    technology, cyclicals, consumer/health, or generic industrial businesses.
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Require P/E ≤ 15, P/B ≤ 1.6, price ≤ 1.8× tangible book (if known), and P/E × P/B ≤ 30.">
                        Banks & Insurers
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Price must not exceed 1.9× tangible book when calculable.">
                        Cyclicals
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Must have positive FCF, FCF margin ≥ 8%, and P/E ≤ 25.">
                        Technology & Communication
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Require gross margin ≥ 30%, P/E ≤ 20, and price ≤ 1.9× tangible book.">
                        Consumer & Healthcare
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Must have dividend yield ≥ 3% and P/E ≤ 18.">
                        Utilities
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Must have positive FCF, P/B ≤ 1.6, dividend yield ≥ 4%, and P/E × P/B ≤ 30.">
                        REITs
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Price must not exceed 1.9× tangible book, when relevant.">
                        Generic Industrial / Misc.
                    </TipText>
                </p>
            </div>
            <div className="stockdiv fade-in">
                <h4>Chapter Fifteen Stocks — Enterprising Investor</h4>

                <p>
                    Chapter Fifteen relaxes rules opens the door to companies that are
                    smaller, faster-moving, and more opportunistic. These filters still enforce financial strength,
                    but they allow more flexibility for growth-oriented businesses.
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Companies must not show structural decline; revenue YoY may dip, but not below -10%.">
                        Revenue Stability
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Shareholder equity must exist and must be positive.">
                        Positive Equity
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Current price and shares outstanding must be present and valid.">
                        Valid Price & Share Data
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Net income (TTM) must be positive.">
                        Profitability Required
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="At least one must be known; if both are missing, the stock fails.">
                        Market Cap or Revenue Required
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="More leverage is tolerated than in Chapter 14, but still capped at a reasonable level.">
                        Debt-to-Equity ≤ 2
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Bare minimum ability to service interest requirements.">
                        Interest Coverage ≥ 1.6
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="If P/B is known (≤ 8), then P/E × P/B must remain under 360.">
                        P/E × P/B Combination Test
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Must fall between 0 and 45.">
                        Three-Year P/E Requirement
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="If earnings velocity is known, it must be positive.">
                        Earnings Trend
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="When FCF and market cap are known, FCF yield must be at least 4%.">
                        Free Cash Flow Yield ≥ 4%
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Free cash flow must cover at least 20% of total debt when this metric is available.">
                        FCF-to-Debt ≥ 0.2
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="If insiders are net sellers and selling exceeds buying by more than 4×, the stock fails.">
                        Insider Alignment
                    </TipText>
                </p>

                <p>
                    Additional sector-based rules apply once a stock passes the universal enterprising screens.
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Require P/E ≤ 15, P/B ≤ 1.8, P/E × P/B ≤ 70. Capital structure differences reduce reliance on D/E.">
                        Banks & Insurers
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Must not show structural deterioration: operating margin trend cannot drop below -2%, and revenue YoY cannot drop below -5%.">
                        Cyclicals
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Must generate positive free cash flow and maintain FCF margin ≥ 5%.">
                        Technology & Communication
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Require debt-to-equity ≤ 1.5, gross margin ≥ 25%, and P/E ≤ 22.">
                        Consumer & Healthcare
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Must have a positive dividend yield; even enterprising investors require income stability here.">
                        Utilities
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Must have positive free cash flow, P/B ≤ 2.0, dividend yield ≥ 3%, debt-to-equity ≤ 1.8, and P/E × P/B ≤ 70.">
                        REITs
                    </TipText>
                </p>

                <p>
                    &nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;
                    <TipText tooltip="Must keep debt-to-equity ≤ 2.0.">
                        Generic Industrial / Misc.
                    </TipText>
                </p>
            </div>
            <div className="stockdiv fade-in">
                <h4>What We Leave Out</h4>
                <p>
                    Not every company qualifies for analysis. If critical information is missing,
                    broken, or inconsistent, the stock is removed before screening. This ensures
                    that our results stay clean and meaningful.
                </p>

                <p>&nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;<strong>Missing Essential Price Data</strong></p>

                <p>&nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;<strong>Incomplete Financial Statements</strong></p>

                <p>&nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;<strong>Not Enough Earnings History</strong></p>

                <p>&nbsp;&nbsp;&nbsp;&nbsp;&#x2022;&nbsp;<strong>Unreliable or Broken Data</strong></p>

                <p>
                    These rules ensure that only companies with clear, consistent, and complete
                    financial information are considered—allowing Graham’s timeless strategy to
                    work as intended.
                </p>
            </div>
        </section>
    );
}

export default Criteria;
